import { useCallback } from 'react';

import { useRouter } from 'expo-router';

import useFoodware from 'hooks/use-foodware';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { IPendingReorder } from 'state/order/types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { routes } from 'utils/routing';
import { isDelivery } from 'utils/service-mode';

export const useReorderButton = (order?: IPendingReorder | null) => {
  const {
    reorder: { handleReorder, setPendingReorder },
  } = useOrderContext();
  const { isStoreOpenAndAvailable, noStoreSelected } = useStoreContext();
  const { serviceMode } = useServiceModeContext();
  const enableReorder = useFlag(LaunchDarklyFlag.ENABLE_REORDER);
  const { isFoodware } = useFoodware();
  const router = useRouter();

  const reorder = useCallback(async () => {
    if (!order) {
      return;
    }

    const filteredReorderCartEntries = order.cart.reorderCartEntries?.filter(
      item => !(item.isDonation || isFoodware(item))
    );

    const filteredOrder: IPendingReorder = {
      ...order,
      cart: {
        ...order.cart,
        reorderCartEntries: filteredReorderCartEntries,
      },
    };

    if (isStoreOpenAndAvailable) {
      handleReorder(filteredOrder);
    } else {
      setPendingReorder(filteredOrder);
      router.navigate(isDelivery(serviceMode) ? routes.deliveryAddress : routes.storeLocator);
    }
  }, [
    order,
    isStoreOpenAndAvailable,
    isFoodware,
    handleReorder,
    setPendingReorder,
    router,
    serviceMode,
  ]);

  const disabled = !enableReorder || (!isStoreOpenAndAvailable && !noStoreSelected);

  return { reorder, disabled };
};
